// MindContext — Trading Mind Layer state management
// Separate from TradingContext. Removing MindProvider restores original behavior.
import React, { createContext, useContext, useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { useMindSettings, useDailyState, usePerformanceState, useBehaviorLog } from '../hooks/useMindFirestore';
import { computeReadinessScore, computeEnergyScore, getReadinessLevel, getEnergyLevel } from '../utils/mindScoring';

const MindContext = createContext();

export const useMind = () => useContext(MindContext);

export const MindProvider = ({ children }) => {
  const { settings, loading: settingsLoading, updateSettings: updateMindSettings } = useMindSettings();
  const { dailyState, loading: dailyLoading, saveDailyState } = useDailyState();
  const { performance, loading: perfLoading, updatePerformance } = usePerformanceState();
  const { logBehavior } = useBehaviorLog();

  // Modal controls
  const [showPreTradeCheck, setShowPreTradeCheck] = useState(false);
  const [showGuardWarning, setShowGuardWarning] = useState(false);
  const [showCooldown, setShowCooldown] = useState(false);
  const [guardWarningData, setGuardWarningData] = useState(null);
  const [pendingOrder, setPendingOrder] = useState(null);
  const [pendingCallback, setPendingCallback] = useState(null);

  // Cooldown timer
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownRef = useRef(null);

  const loading = settingsLoading || dailyLoading || perfLoading;
  const isCheckedIn = !!dailyState;

  // Computed scores
  const readinessScore = useMemo(() => {
    if (!dailyState) return 50; // default when not checked in
    return computeReadinessScore({
      mood: dailyState.mood || 3,
      focus: dailyState.focus || 3,
      stress: dailyState.stress || 3,
      sleep: dailyState.sleep || 3,
    });
  }, [dailyState]);

  const energyScore = useMemo(() => {
    if (!settings || !performance) return readinessScore;
    return computeEnergyScore(readinessScore, performance, settings);
  }, [readinessScore, performance, settings]);

  const readinessLevel = useMemo(() => getReadinessLevel(readinessScore), [readinessScore]);
  const energyLevel = useMemo(() => getEnergyLevel(energyScore), [energyScore]);

  // Cooldown active check
  const cooldownActive = useMemo(() => {
    if (!performance?.cooldownUntil) return false;
    return Date.now() < performance.cooldownUntil;
  }, [performance]);

  // Cooldown countdown timer (1s interval)
  useEffect(() => {
    if (!cooldownActive || !performance?.cooldownUntil) {
      setCooldownRemaining(0);
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }

    const tick = () => {
      const remaining = Math.max(0, performance.cooldownUntil - Date.now());
      setCooldownRemaining(remaining);
      if (remaining <= 0 && cooldownRef.current) {
        clearInterval(cooldownRef.current);
      }
    };

    tick();
    cooldownRef.current = setInterval(tick, 1000);
    return () => { if (cooldownRef.current) clearInterval(cooldownRef.current); };
  }, [cooldownActive, performance]);

  // Show pre-trade check on first visit if enabled and not checked in
  useEffect(() => {
    if (!loading && settings?.enablePreTradeCheck && !isCheckedIn) {
      setShowPreTradeCheck(true);
    }
  }, [loading, settings, isCheckedIn]);

  // Submit daily check-in
  const submitDailyCheck = useCallback(async ({ mood, focus, stress, sleep, intention }) => {
    const score = computeReadinessScore({ mood, focus, stress, sleep });
    await saveDailyState({
      mood, focus, stress, sleep,
      intention,
      readinessScore: score,
      checkedInAt: Date.now(),
    });
    setShowPreTradeCheck(false);
  }, [saveDailyState]);

  // Skip pre-trade check
  const skipDailyCheck = useCallback(() => {
    logBehavior({ type: 'PRE_TRADE_CHECK_SKIPPED' });
    setShowPreTradeCheck(false);
  }, [logBehavior]);

  // Update performance after a trade result
  const updatePerformanceAfterTrade = useCallback(async (tradeResult) => {
    if (!performance || !settings) return;

    const pnl = tradeResult.pnl || 0;
    const isLoss = pnl < 0;
    const newConsecutiveLosses = isLoss ? (performance.consecutiveLosses || 0) + 1 : 0;
    const newDailyPnL = (performance.dailyPnL || 0) + pnl;
    const newTradesCount = (performance.tradesToday || 0) + 1;

    // Build recent trades (keep last 10 for revenge detection)
    const recentTrades = [
      {
        tradingsymbol: tradeResult.tradingsymbol,
        symboltoken: tradeResult.symboltoken,
        pnl,
        timestamp: Date.now(),
      },
      ...(performance.recentTrades || []).slice(0, 9),
    ];

    const updates = {
      consecutiveLosses: newConsecutiveLosses,
      dailyPnL: newDailyPnL,
      tradesToday: newTradesCount,
      lastTradeTime: Date.now(),
      lastTradeSymbol: tradeResult.tradingsymbol || tradeResult.symboltoken,
      lastTradePnL: pnl,
      recentTrades,
    };

    // Trigger cooldown if loss streak threshold hit
    if (newConsecutiveLosses >= settings.consecutiveLossThreshold && settings.enableCooldown) {
      updates.cooldownUntil = Date.now() + (settings.cooldownMinutes * 60 * 1000);
      setShowCooldown(true);
    }

    await updatePerformance(updates);
  }, [performance, settings, updatePerformance]);

  // Guard warning modal controls
  const showGuardModal = useCallback((guards, order, onOverride, onCancel) => {
    setGuardWarningData({ guards });
    setPendingOrder(order);
    setPendingCallback(() => ({ onOverride, onCancel }));
    setShowGuardWarning(true);
  }, []);

  const handleGuardOverride = useCallback(() => {
    const guards = guardWarningData?.guards || [];
    guards.forEach(g => {
      logBehavior({ type: 'GUARD_OVERRIDDEN', guardId: g.id, order: pendingOrder });
    });
    setShowGuardWarning(false);
    if (pendingCallback?.onOverride) pendingCallback.onOverride();
    setPendingOrder(null);
    setPendingCallback(null);
    setGuardWarningData(null);
  }, [guardWarningData, pendingOrder, pendingCallback, logBehavior]);

  const handleGuardCancel = useCallback(() => {
    const guards = guardWarningData?.guards || [];
    guards.forEach(g => {
      logBehavior({ type: 'GUARD_RESPECTED', guardId: g.id, order: pendingOrder });
    });
    setShowGuardWarning(false);
    if (pendingCallback?.onCancel) pendingCallback.onCancel();
    setPendingOrder(null);
    setPendingCallback(null);
    setGuardWarningData(null);
  }, [guardWarningData, pendingOrder, pendingCallback, logBehavior]);

  // Cooldown override
  const overrideCooldown = useCallback(async () => {
    logBehavior({ type: 'COOLDOWN_OVERRIDDEN' });
    await updatePerformance({ cooldownUntil: null });
    setShowCooldown(false);
  }, [updatePerformance, logBehavior]);

  const value = {
    // State
    settings, dailyState, performance,
    loading, isCheckedIn,

    // Scores
    readinessScore, energyScore,
    readinessLevel, energyLevel,

    // Cooldown
    cooldownActive, cooldownRemaining,

    // Actions
    submitDailyCheck, skipDailyCheck,
    updatePerformanceAfterTrade,
    updateMindSettings,
    logBehavior,

    // Modal state
    showPreTradeCheck, setShowPreTradeCheck,
    showGuardWarning, guardWarningData,
    showCooldown, setShowCooldown,
    pendingOrder,

    // Modal actions
    showGuardModal,
    handleGuardOverride, handleGuardCancel,
    overrideCooldown,
  };

  return (
    <MindContext.Provider value={value}>
      {children}
    </MindContext.Provider>
  );
};
